import { useState } from "react";
import { RowData, BulkAction } from "../types/collapsibleTable";

interface UseCollapsibleSelectionProps {
    data: RowData[];
    keyChildren: string;
    keyItem: string;
    keyChildrenItem?: string;
    selectedIds: string[];
    selectedChildIds: string[];
    onSelectionChange?: (ids: string[], detailIds: string[], selectedRows: RowData[], selectedChilds: any[]) => void;
    isRowSelectable?: (row: RowData) => boolean;
    isChildRowSelectable?: (detail: any) => boolean;
    allowSelectParentWithoutChildren?: boolean;
}

export function useCollapsibleSelection({
    data,
    keyChildren,
    keyItem,
    keyChildrenItem,
    selectedIds,
    selectedChildIds,
    onSelectionChange,
    isRowSelectable = () => true,
    isChildRowSelectable = () => true,
    allowSelectParentWithoutChildren = false,
}: UseCollapsibleSelectionProps) {

    const controlledSelectedIds = selectedIds || [];
    const controlledSelectedChildIds = selectedChildIds || [];
    const [totalRowsSelected, setTotalRowsSelected] = useState(0);

    const rowById: Record<string, RowData> = {};
    data.forEach(row => { rowById[row[keyItem] as string] = row; });

    const childById: Record<string, any> = {};
    data.forEach(row => {
        if (row[keyChildren] && keyChildrenItem) {
            (row[keyChildren] as any[]).forEach((detail: any) => {
                childById[detail[keyChildrenItem] as string] = detail;
            });
        }
    });

    function updateSelection(newIds: string[], newDetailIds: string[]) {
        if (onSelectionChange) {
            const selectedRows = newIds.map(id => rowById[id]).filter(Boolean) as RowData[];
            const selectedDetailRows = newDetailIds.map(id => childById[id]).filter(Boolean);

            const totalRowsNotChildren = selectedRows.reduce((acc, row) => {
                if(row[keyChildren].length > 0){
                    return acc + 0;
                }
                return acc + 1;
            }, 0)

            setTotalRowsSelected(totalRowsNotChildren + selectedDetailRows.length);
            onSelectionChange(newIds, newDetailIds, selectedRows, selectedDetailRows);
        }
    };

    function getSelectableChildRows(row: RowData): string[] {
        if (!row[keyChildren] || !keyChildrenItem) return [];

        return (row[keyChildren] as any[])
            .filter((detail: any) => isChildRowSelectable(detail))
            .map((detail: any) => detail[keyChildrenItem] as string);
    };

    function getAllSelectableChildIds(): string[] {
        if (!keyChildrenItem) return [];

        return data.flatMap(row => {
            const children = row[keyChildren];
            if (!children) return [];
            const selectableDetails = (children as any[]).filter((detail: any) => isChildRowSelectable(detail))
            return selectableDetails.map((detail: any) => detail[keyChildrenItem] as string)
        });
    };

    function getPageSelectableIds(): string[] {
        return data
            .filter(row => {
                if (!isRowSelectable(row)) return false;

                const hasChildren =
                    row[keyChildren] &&
                    keyChildrenItem &&
                    getSelectableChildRows(row).length > 0;

                // 🔹 permite pai sem filhos se flag estiver ativa
                return hasChildren || allowSelectParentWithoutChildren;
            })
            .map(row => row[keyItem] as string);
    }


    function isRowDisabled(row: RowData): boolean { return !isRowSelectable(row) }

    function isRowFullyChecked(row: RowData): boolean {
        const childrenIds = getSelectableChildRows(row);

        if (!childrenIds.length) {
            return allowSelectParentWithoutChildren
                ? controlledSelectedIds.includes(row[keyItem] as string)
                : false;
        }

        return childrenIds.every(id =>
            controlledSelectedChildIds.includes(id)
        );
    }


    function isRowIndeterminate(row: RowData): boolean {
        const detailIds = getSelectableChildRows(row);
        if (!detailIds.length) return false;
        const checkedCount = detailIds.filter((id: string) => controlledSelectedChildIds.includes(id)).length;
        return checkedCount > 0 && checkedCount < detailIds.length;
    };

    function handleRowCheckbox(row: RowData) {
        const id = row[keyItem] as string;
        const hasChildren =
            row[keyChildren] &&
            keyChildrenItem &&
            getSelectableChildRows(row).length > 0;

        // 🔹 Se não tem filhos e a flag não permite → ignora
        if (!hasChildren && !allowSelectParentWithoutChildren) {
            return;
        }

        const newSelected = controlledSelectedIds.includes(id)
            ? controlledSelectedIds.filter(sid => sid !== id)
            : [...controlledSelectedIds, id];

        updateSelection(newSelected, controlledSelectedChildIds);
    }


    function handleAllChildrenCheckbox(row: RowData) {
        const rowId = row[keyItem] as string;
        const childrenIds = getSelectableChildRows(row);
        const hasChildren = childrenIds.length > 0;

        // 🔹 CASO 1: NÃO TEM FILHOS
        if (!hasChildren) {
            // Se não permitir selecionar pai sem filhos, não faz nada
            if (!allowSelectParentWithoutChildren) return;

            const isSelected = controlledSelectedIds.includes(rowId);
            const newSelected = isSelected
                ? controlledSelectedIds.filter(id => id !== rowId)
                : [...controlledSelectedIds, rowId];

            updateSelection(newSelected, controlledSelectedChildIds);
            return;
        }

        // 🔹 CASO 2: TEM FILHOS (comportamento atual)
        const allChecked = childrenIds.every(id =>
            controlledSelectedChildIds.includes(id)
        );

        let newSelected = controlledSelectedIds.filter(id => id !== rowId);
        let newSelectedChildrenIds = controlledSelectedChildIds.filter(
            id => !childrenIds.includes(id)
        );

        if (!allChecked) {
            newSelected = [...newSelected, rowId];
            newSelectedChildrenIds = Array.from(
                new Set([...newSelectedChildrenIds, ...childrenIds])
            );
        }

        updateSelection(newSelected, newSelectedChildrenIds);
    }


    function handleChildCheckbox(row: RowData, children: any) {
        if (!keyChildrenItem) return;

        const childrenId = children[keyChildrenItem] as string;

        let newSelectedChildrensIds = controlledSelectedChildIds.includes(childrenId)
            ? controlledSelectedChildIds.filter(id => id !== childrenId)
            : [...controlledSelectedChildIds, childrenId];

        const selectableChildrens = getSelectableChildRows(row);
        const allChecked = selectableChildrens.every((id: string) => newSelectedChildrensIds.includes(id));

        let newSelected = controlledSelectedIds.filter(id => id !== row[keyItem] as string);
        if (allChecked) newSelected = [...newSelected, row[keyItem] as string];

        updateSelection(newSelected, newSelectedChildrensIds);
    };

    function handleSelectAllPage() {
        const pageIds = getPageSelectableIds();

        if (keyChildrenItem) {
            const pageChildrenIds = getAllSelectableChildIds();
            const isAllPageChildrenChecked = pageChildrenIds.length > 0 && pageChildrenIds.every(id => controlledSelectedChildIds.includes(id));

            let newSelected = controlledSelectedIds.filter(id => !pageIds.includes(id));
            let newSelectedDetailIds = controlledSelectedChildIds.filter(id => !pageChildrenIds.includes(id));

            if (!isAllPageChildrenChecked) {
                newSelected = Array.from(new Set([...newSelected, ...pageIds]));
                newSelectedDetailIds = Array.from(new Set([...newSelectedDetailIds, ...pageChildrenIds]));
            }

            updateSelection(newSelected, newSelectedDetailIds);

        } else {
            const isAllPageChecked = pageIds.length > 0 && pageIds.every(id => controlledSelectedIds.includes(id));
            let newSelected: string[];

            if (!isAllPageChecked) {
                newSelected = Array.from(new Set([...controlledSelectedIds, ...pageIds]));
            } else {
                newSelected = controlledSelectedIds.filter(id => !pageIds.includes(id));
            }
            updateSelection(newSelected, []);
        }
    };

    function isSelectAllIndeterminate(): boolean {
        const pageIds = getPageSelectableIds();

        if (keyChildrenItem) {
            const pageChildrenIds = getAllSelectableChildIds();
            if (pageChildrenIds.length === 0) return false;

            const checkedCount = pageChildrenIds.filter(id => controlledSelectedChildIds.includes(id)).length;
            return checkedCount > 0 && checkedCount < pageChildrenIds.length;

        } else {
            if (pageIds.length === 0) return false;

            const checkedCount = pageIds.filter(id => controlledSelectedIds.includes(id)).length;
            return checkedCount > 0 && checkedCount < pageIds.length;
        }
    }

    function isSelectAllChecked(): boolean {
        if (keyChildrenItem) {
            const pageChildrenIds = getAllSelectableChildIds();
            if (pageChildrenIds.length === 0) return false;

            return pageChildrenIds.every(id => controlledSelectedChildIds.includes(id));
        } else {
            const pageIds = getPageSelectableIds();
            if (pageIds.length === 0) return false;

            return pageIds.every(id => controlledSelectedIds.includes(id));
        }
    }

    function getIsDisabledBulkActions(): boolean {
        const hasChildSelection =
            Boolean(keyChildrenItem) && controlledSelectedChildIds.length > 0;

        const hasParentSelection =
            controlledSelectedIds.length > 0;

        if (keyChildrenItem) {
            if (hasChildSelection) return false;
            if (allowSelectParentWithoutChildren && hasParentSelection) return false;
            return true;
        }

        return !hasParentSelection;
    }


    const executeBulkAction = (action: BulkAction) => {
        const selectedRowsObjects = controlledSelectedIds.map(id => rowById[id]).filter(Boolean) as RowData[];
        const selectedDetailRowsObjects = controlledSelectedChildIds.map(id => childById[id]).filter(Boolean);
        action.act(selectedRowsObjects, selectedDetailRowsObjects);
    }

    return {
        updateSelection,
        controlledSelectedIds,
        controlledSelectedChildIds,
        isRowFullyChecked,
        isRowIndeterminate,
        isRowDisabled,
        handleRowCheckbox,
        handleAllChildrenCheckbox,
        handleChildCheckbox,
        handleSelectAllPage,
        getAllSelectableChildIds,
        getPageSelectableIds,
        isSelectAllIndeterminate,
        isSelectAllChecked,
        isDisabledBulkActions: getIsDisabledBulkActions(),
        totalRowsSelected,
        executeBulkAction,
        rowById,
        childById
    };
}